import { Injectable, inject } from '@angular/core';
import { Observable, map, of, switchMap, take } from 'rxjs';

import {
  AuxiliaryDataBundle,
  AuxiliaryDataFetchDetails
} from '@gfe/core/models/entities/entity-visualization-interface';
import {
  PlanFilter,
  PlanFilterOperator
} from '@gfe/core/models/entity/entity-selector-request.model';

import { SelectorsService } from '../selectors/selectors.service';

/** Resolution context that scopes the cache and the backend query. */
export interface AuxiliaryDataContext {
  dataSpaceId: number;
  planId?: string;
  planOperator?: PlanFilterOperator;
}

/**
 * Resolves the related entities a visualization builder needs (sites for route colours and
 * coordinates, and so on), caching them per uid.
 *
 * The cache is what makes resolving at selection time affordable: the visualization path is
 * re-entered on every selection change, and each change usually adds a handful of uids to a
 * set already resolved. Without it, selecting rows one by one would refetch the whole set each
 * time. Mirrors `ReferenceNamesService`, which caches uid → name the same way.
 */
@Injectable({ providedIn: 'root' })
export class AuxiliaryDataService {
  private readonly selectorsService = inject(SelectorsService);

  /** cacheKey(context) -> (versionName -> (uid -> entity)). */
  private readonly cache = new Map<string, Map<string, Map<string, unknown>>>();

  resolve$(
    fetchDetails: AuxiliaryDataFetchDetails,
    ctx: AuxiliaryDataContext
  ): Observable<AuxiliaryDataBundle> {
    const wanted = Object.entries(fetchDetails.versionNameAndUidsPerType);
    const missing = this.missingFrom(fetchDetails, ctx);
    if (!missing) {
      return of(this.bundleFor(wanted, ctx));
    }

    const [primary, fallback] = this.planFilters(ctx);
    return this.fetchInto$(missing, ctx, primary).pipe(
      switchMap(() => {
        const stillMissing = fallback && this.missingFrom(missing, ctx);
        return stillMissing ? this.fetchInto$(stillMissing, ctx, fallback) : of(undefined);
      }),
      map(() => this.bundleFor(wanted, ctx))
    );
  }

  /** Drops everything; call when an entity was written and the cached copies may be stale. */
  invalidate(): void {
    this.cache.clear();
  }

  /**
   * Rebuilds the bundle from the cache for exactly the uids that were asked for. A uid the
   * backend did not return is simply absent — builders already treat a missing entity as
   * unknown, so a partial bundle degrades rather than throws.
   */
  private bundleFor(
    wanted: [string, [string, string[]]][],
    ctx: AuxiliaryDataContext
  ): AuxiliaryDataBundle {
    const entitiesArrayPerVersionName: Record<string, unknown[]> = {};
    for (const [, [versionName, uids]] of wanted) {
      const known = this.versionCache(ctx, versionName);
      const entities = entitiesArrayPerVersionName[versionName] ?? [];
      for (const uid of new Set(uids)) {
        const entity = known.get(uid);
        if (entity !== undefined) entities.push(entity);
      }
      entitiesArrayPerVersionName[versionName] = entities;
    }
    return { entitiesArrayPerVersionName };
  }

  /**
   * A `NOT_IN` context lists the rows a plan can still import, but what those rows reference
   * is often already in the plan — and the plan holds its own copies, possibly edited, plus
   * rows that exist nowhere else. So references are read from the plan first, and only the
   * uids it lacks from the global data outside it: together the two cover every uid, with
   * the plan's copy winning.
   */
  private planFilters(
    ctx: AuxiliaryDataContext
  ): [PlanFilter | undefined, PlanFilter | undefined] {
    if (ctx.planId == null) return [undefined, undefined];
    const inPlan = { id: ctx.planId, operator: PlanFilterOperator.IN };
    return ctx.planOperator === PlanFilterOperator.NOT_IN
      ? [inPlan, { id: ctx.planId, operator: PlanFilterOperator.NOT_IN }]
      : [inPlan, undefined];
  }

  /** The uids of `details` not yet cached for `ctx`, or `undefined` when there are none. */
  private missingFrom(
    details: AuxiliaryDataFetchDetails,
    ctx: AuxiliaryDataContext
  ): AuxiliaryDataFetchDetails | undefined {
    const missing: AuxiliaryDataFetchDetails = { versionNameAndUidsPerType: {} };
    for (const [type, [versionName, uids]] of Object.entries(
      details.versionNameAndUidsPerType
    )) {
      const known = this.versionCache(ctx, versionName);
      const absent = Array.from(new Set(uids)).filter((uid) => !known.has(uid));
      if (absent.length > 0) {
        missing.versionNameAndUidsPerType[type] = [versionName, absent];
      }
    }
    return Object.keys(missing.versionNameAndUidsPerType).length > 0 ? missing : undefined;
  }

  private fetchInto$(
    details: AuxiliaryDataFetchDetails,
    ctx: AuxiliaryDataContext,
    planFilter: PlanFilter | undefined
  ): Observable<void> {
    return this.selectorsService
      .fetchAuxiliaryData$(details, ctx.dataSpaceId, planFilter)
      .pipe(
        take(1),
        map((fetched) => {
          for (const [versionName, entities] of Object.entries(
            fetched.entitiesArrayPerVersionName
          )) {
            const known = this.versionCache(ctx, versionName);
            for (const entity of entities) {
              const uid = (entity as { uid?: string })?.uid;
              if (uid != null) known.set(uid, entity);
            }
          }
        })
      );
  }

  private versionCache(
    ctx: AuxiliaryDataContext,
    versionName: string
  ): Map<string, unknown> {
    const key = `${ctx.dataSpaceId}|${ctx.planId ?? ''}|${ctx.planOperator ?? ''}`;
    let byVersion = this.cache.get(key);
    if (!byVersion) {
      byVersion = new Map();
      this.cache.set(key, byVersion);
    }
    let byUid = byVersion.get(versionName);
    if (!byUid) {
      byUid = new Map();
      byVersion.set(versionName, byUid);
    }
    return byUid;
  }
}
